import type { ICellRendererParams } from 'ag-grid-community';
import { Button } from 'react-bootstrap';
import type { NavigateFunction } from 'react-router-dom';
import SubtleBadge from '@/components/common/SubtleBadge';
import {
  bucketLabel,
  type InspectCoverageBucket,
  type InspectDetail,
} from '../lib/inspect-detail';
import { inspectDetailToSnapshot } from '../lib/seo-ai-prompt';
import { copyText, decodeUrlForDisplay, formatTime } from '../lib/utils';
import {
  navigateToIndexWithPrefill,
  navigateToInspectWithPrefill,
} from '../lib/url-list-prefill';
import { ActionTip } from '../components/ActionTip';
import { CoverageHelpTip } from '../components/CoverageHelpTip';
import { IndexerIcon } from '../components/IndexerIcon';
import { SearchConsoleIcon } from '../components/SearchConsoleIcon';
import { SeoAiAdviceAction } from '../components/SeoAiAdvice';
import { StaleCrawlBadge } from '../lib/stale-crawl';

function bucketSubtleTone(bucket: InspectCoverageBucket) {
  if (bucket === 'indexed') return 'success';
  if (bucket === 'error') return 'danger';
  return 'warning';
}

export function inspectResultColumnConfig({
  navigate,
  onHistory,
}: {
  navigate: NavigateFunction;
  onHistory: (url: string) => void;
}) {
  return [
    {
      field: 'url',
      title: 'آدرس',
      type: 'string',
      flex: 1.4,
      minWidth: 220,
      searchable: true,
      cellClass: 'cursor-pointer',
      cellRenderer: (params: ICellRendererParams<InspectDetail>) => {
        const row = params.data;
        if (!row) return null;
        const decoded = decodeUrlForDisplay(row.url);
        return (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none text-start d-flex align-items-center min-w-0 w-100 h-100 py-1"
            title={decoded}
            onClick={(event) => {
              event.stopPropagation();
              onHistory(row.url);
            }}
          >
            <span className="mono if-url-clip text-primary">{decoded}</span>
          </button>
        );
      },
    },
    {
      field: 'bucket',
      title: 'وضعیت ایندکس',
      type: 'string',
      width: 140,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<InspectDetail>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <span className="d-flex align-items-center h-100 w-100">
            <SubtleBadge
              bg={bucketSubtleTone(row.bucket)}
              pill
              className="fs--2"
              style={{ lineHeight: 1.35 }}
            >
              {bucketLabel(row.bucket)}
            </SubtleBadge>
          </span>
        );
      },
    },
    {
      field: 'coverageState',
      title: 'پوشش',
      type: 'string',
      flex: 1.2,
      minWidth: 260,
      searchable: true,
      cellClass: 'd-flex align-items-center',
      cellRenderer: (params: ICellRendererParams<InspectDetail>) => {
        const row = params.data;
        if (!row) return null;
        const coverage = row.coverageState || row.errorDetail;
        if (!coverage) return '—';
        return (
          <span className="d-inline-flex align-items-center flex-nowrap gap-1 mw-100 min-w-0">
            <span className="text-truncate min-w-0" title={coverage}>
              {coverage}
            </span>
            {row.coverageState ? (
              <CoverageHelpTip coverageState={row.coverageState} />
            ) : null}
          </span>
        );
      },
    },
    {
      field: 'lastCrawlTime',
      title: 'تاریخ خزش گوگل',
      type: 'string',
      width: 220,
      minWidth: 180,
      searchable: false,
      cellClass: 'd-flex align-items-center',
      cellRenderer: (params: ICellRendererParams<InspectDetail>) => {
        const lastCrawlTime = params.data?.lastCrawlTime;
        const staleCrawl = params.data?.staleCrawl;
        if (!lastCrawlTime && !staleCrawl) return '—';
        return (
          <span className="d-inline-flex align-items-center flex-nowrap gap-1 mw-100 min-w-0">
            {lastCrawlTime ? (
              <span className="text-nowrap">{formatTime(lastCrawlTime)}</span>
            ) : (
              <span className="text-secondary">—</span>
            )}
            {staleCrawl ? <StaleCrawlBadge staleCrawl={staleCrawl} /> : null}
          </span>
        );
      },
    },
    {
      field: '_actions',
      title: 'عملیات',
      width: 200,
      maxWidth: 220,
      sortable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      headerClass: 'text-center',
      cellClass: 'd-flex align-items-center justify-content-center',
      cellRenderer: (params: ICellRendererParams<InspectDetail>) => {
        const row = params.data;
        if (!row) return null;
        const decoded = decodeUrlForDisplay(row.url);

        return (
          <div className="if-table-actions justify-content-center">
            <ActionTip tip="جزئیات + تاریخچه">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="جزئیات + تاریخچه"
                onClick={() => onHistory(row.url)}
              >
                <IndexerIcon name="clock-history" />
              </Button>
            </ActionTip>
            <SeoAiAdviceAction
              url={row.url}
              snapshots={[inspectDetailToSnapshot(row)]}
              disabled={row.bucket === 'indexed'}
            />
            {row.inspectLink ? (
              <ActionTip tip="باز کردن در Search Console">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="if-table-action if-table-action--brand"
                  aria-label="باز کردن در Search Console"
                  href={row.inspectLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <SearchConsoleIcon />
                </Button>
              </ActionTip>
            ) : null}
            <ActionTip tip="درخواست ایندکس">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="درخواست ایندکس"
                onClick={() =>
                  navigateToIndexWithPrefill(navigate, [row.url], 'inspect-results')
                }
              >
                <IndexerIcon name="card-text" />
              </Button>
            </ActionTip>
            <ActionTip tip="وضعیت ایندکس">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="وضعیت ایندکس"
                onClick={() =>
                  navigateToInspectWithPrefill(
                    navigate,
                    [row.url],
                    'inspect-results',
                  )
                }
              >
                <IndexerIcon name="search" />
              </Button>
            </ActionTip>
            <ActionTip tip="کپی">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="کپی"
                onClick={() => void copyText(decoded)}
              >
                <IndexerIcon name="copy" />
              </Button>
            </ActionTip>
          </div>
        );
      },
    },
  ];
}
